'use client';

import { ReactNode } from 'react';

interface SectionProps {
  /**
   * The section title
   */
  title?: string;
  
  /**
   * Optional subtitle or description
   */
  description?: string;
  
  /**
   * Optional tag text above title
   */
  tagText?: string;
  
  /**
   * Background style - default is 'white'
   */
  background?: 'white' | 'light' | 'gradient' | 'dark';
  
  /**
   * Children components to render inside the section
   */
  children: ReactNode;
  
  /**
   * Optional: Override wave divider display
   */
  showWaveDivider?: boolean;
  
  /**
   * Optional classNames to override default styles
   */
  className?: string;
  
  /**
   * Optional center text alignment
   */
  centerText?: boolean;
  
  /**
   * Controls vertical padding on mobile (default is 'default')
   */
  mobilePadding?: 'compact' | 'default' | 'spacious';
}

export default function Section({
  title,
  description,
  tagText,
  background = 'white',
  children,
  showWaveDivider = false,
  className = '',
  centerText = false,
  mobilePadding = 'default',
}: SectionProps) {
  // Set background classes based on the background prop
  const getBgClasses = () => {
    switch (background) {
      case 'light':
        return 'bg-gradient-to-br from-[#f4fbfa] to-white';
      case 'gradient':
        return 'bg-gradient-to-b from-white to-slate-50';
      case 'dark':
        return 'bg-gradient-to-br from-slate-900 via-slate-800 to-[#1e5e52] text-white';
      default:
        return 'bg-white';
    }
  };

  // Set padding classes based on mobilePadding prop
  const getPaddingClasses = () => {
    switch (mobilePadding) {
      case 'compact':
        return 'py-8 sm:py-12 md:py-16';
      case 'spacious':
        return 'py-16 sm:py-20 md:py-24';
      default:
        return 'py-12 sm:py-16 md:py-20';
    }
  };

  const bgClasses = getBgClasses();
  const paddingClasses = getPaddingClasses();
  const textAlignClass = centerText ? 'text-center' : '';
  
  return (
    <section className={`${paddingClasses} px-4 sm:px-6 relative ${bgClasses} ${className}`}>
      <div className="max-w-5xl mx-auto">
        {(title || tagText || description) && (
          <div className={`${centerText ? 'text-center' : ''} mb-8 sm:mb-10 md:mb-16`}>
            {tagText && (
              <span className="inline-block bg-[#38b5a0]/10 text-[#38b5a0] font-medium px-3 sm:px-4 py-1 rounded-full text-xs sm:text-sm mb-3 sm:mb-4">
                {tagText.toUpperCase()}
              </span>
            )}
            
            {title && (
              <h2 className={`text-xl sm:text-2xl md:text-4xl font-bold mb-2 sm:mb-3 text-[#1a2942] ${background === 'dark' ? 'text-white' : ''}`}>
                {title}
              </h2>
            )}
            
            {description && (
              <p className={`text-sm sm:text-base ${background === 'dark' ? 'text-white opacity-90' : 'text-[#455a73]'} ${centerText ? 'max-w-2xl mx-auto' : ''}`}>
                {description}
              </p>
            )}
          </div>
        )}
        
        {children}
      </div>
      
      {showWaveDivider && (
        <div className="absolute bottom-0 left-0 w-full overflow-hidden">
          <svg
            data-name="Layer 1"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 1200 120"
            preserveAspectRatio="none"
            className="w-full h-[40px] sm:h-[60px]"
          >
            <path
              d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
              className="fill-white"
            ></path>
          </svg>
        </div>
      )}
    </section>
  );
} 